/**
 * Wire contracts for the /auth/* endpoints.
 *
 * CORTEX delegates login + MFA to Cloudflare Access (Option B in PR
 * #4b). Therefore there is NO `/auth/login` or `/auth/mfa/verify` —
 * Cloudflare owns those. The two endpoints we expose are:
 *
 *   GET  /auth/me      — return the currently-authenticated principal,
 *                        roles, permissions, and authority limits.
 *   POST /auth/logout  — invalidate the CORTEX session row.
 *
 * Browser-side logout against Cloudflare Access itself is a separate
 * step: the SPA redirects to `/cdn-cgi/access/logout` on the same
 * origin. That returns the user to Cloudflare's login page next request.
 */

import { z } from 'zod';

const IdentityProviderSchema = z.enum(['cloudflare-otp', 'entra-id', 'google', 'service-account']);

const EntityTypeSlugSchema = z.enum([
  'insurer',
  'brokerage',
  'client',
  'assistance-company',
  'reinsurer',
  'loss-adjuster',
  'tpa',
]);

const RoleSummarySchema = z.object({
  slug: z.string().min(1),
  displayName: z.string().min(1),
});

const AuthoritySchema = z.object({
  /** Numeric string (e.g. "5000000.00") to preserve precision; NULL = no bind authority. */
  bindLimit: z.string().nullable(),
  discretionaryRangePct: z.string().nullable(),
  hasUserOverride: z.boolean(),
});

const SessionSummarySchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});

export const AuthMeResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().min(1),
    identityProvider: IdentityProviderSchema,
    entity: z.object({
      id: z.string().uuid(),
      name: z.string().min(1),
      typeSlug: EntityTypeSlugSchema,
    }),
  }),
  session: SessionSummarySchema,
  permissions: z.object({
    keys: z.array(z.string().min(1)).readonly(),
    roles: z.array(RoleSummarySchema).readonly(),
    authority: AuthoritySchema,
  }),
});

export const AuthLogoutResponseSchema = z.object({
  loggedOut: z.literal(true),
  /** URL the SPA should redirect to in order to complete CF Access logout. */
  cfLogoutUrl: z.string().url(),
});

export type AuthMeResponse = z.infer<typeof AuthMeResponseSchema>;
export type AuthLogoutResponse = z.infer<typeof AuthLogoutResponseSchema>;
